import { useEffect, useRef } from "react"
import { Linking, Platform } from "react-native"

import { useGlobalSearchParams } from "expo-router"
import { usePostHog } from "posthog-react-native"

import { useAuth } from "src/context/AuthContext"
import { useChannelLink } from "src/context/ChannelLinkContext"

function normalizeChannelParam(value: string | string[] | undefined): string | null {
  if (value === undefined) return null
  const raw = Array.isArray(value) ? value[0] : value
  if (typeof raw !== "string") return null
  const trimmed = raw.trim()
  return trimmed.length > 0 ? trimmed : null
}

/** Extracts `channel` from a native deeplink URL such as `sidekick://?channel=<id>`. */
function parseChannelFromDeeplink(url: string): string | null {
  try {
    const parsed = new URL(url)
    const raw = parsed.searchParams.get("channel")?.trim()
    return raw && raw.length > 0 ? raw : null
  } catch {
    return null
  }
}

/**
 * Persists `?channel=<uuid>` from the URL into MMKV so it survives auth redirects,
 * and emits analytics for channel-link entry.
 *
 * Routing decisions for unauthenticated users are handled by:
 *   - `(app)/_layout.tsx`       — detects the ?channel= param and redirects to /unauth/channel-splash/[id]
 *   - `channel-splash/[id].tsx` — fetches channel_info and routes to the library or login
 */
export function ChannelLinkCapture() {
  const params = useGlobalSearchParams<{ channel?: string | string[] }>()
  const { setPendingChannelId, clearPendingChannelId } = useChannelLink()
  const { isAuthenticated } = useAuth()
  const posthog = usePostHog()
  const lastReportedChannelRef = useRef<string | null>(null)
  // Tracks whether a channel param was ever present in this app session.
  // Prevents clearing the stored channel ID during auth redirects that drop the param
  // after the user arrived via a deeplink.
  const hadChannelParamThisSessionRef = useRef(false)

  useEffect(() => {
    const channelId = normalizeChannelParam(params.channel)
    if (!channelId) {
      lastReportedChannelRef.current = null
      // Only clear when the param was absent from the very start of this session,
      // i.e. the user loaded the app without a channel deeplink (not a post-deeplink redirect).
      if (!hadChannelParamThisSessionRef.current) {
        clearPendingChannelId()
      }
      return
    }

    hadChannelParamThisSessionRef.current = true
    setPendingChannelId(channelId)

    if (lastReportedChannelRef.current === channelId) return
    lastReportedChannelRef.current = channelId

    posthog?.capture("channel_link_accessed", { channelId, isAuthenticated })

    if (!isAuthenticated) {
      posthog?.capture("channel_link_auth_required", { channelId })
    }
  }, [params.channel, setPendingChannelId, clearPendingChannelId, isAuthenticated, posthog])

  // Native: listen directly to Linking URL events so that a channel deeplink received
  // while the app is backgrounded updates the pending channel ID regardless of whether
  // Expo Router preserves the query param through its unauthenticated redirect.
  useEffect(() => {
    if (Platform.OS === "web") return

    const subscription = Linking.addEventListener("url", ({ url }) => {
      const channelId = parseChannelFromDeeplink(url)
      if (channelId) {
        setPendingChannelId(channelId)
      }
    })

    return () => subscription.remove()
  }, [setPendingChannelId])

  return null
}
